import {inject, Injectable, signal} from '@angular/core';
import {HibernationService} from './hibernation.service';
import {FetchTimeoutError} from './fetch-with-timeout';

interface HibernationRetryParams {
  // Mutable request-local flags used to enforce "at most one hibernation retry".
  _hibernationRetried?: boolean;
  _hibernationRecoveryAttempted?: boolean;
}

export function shouldAttemptHibernationRecovery(
  status: number | undefined | null,
  sleeping: boolean = false,
  isStatusRequest: boolean = false
) {
  // Shared status-code policy for wake retries.
  // Used by HibernationFlowService runtime logic and unit tests.
  // Common "backend not ready / no upstream" signals during scale-from-zero.
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }
  // 404 is often a real "not found". Only treat it as hibernation when we are already in a wake flow
  // (splash/overlay), or when checking /status itself.
  if (status === 404) {
    return sleeping || isStatusRequest;
  }
  // 500 can happen during wake-up while upstream dependencies are still initializing,
  // but it can also be a real backend failure. Only treat it as hibernation-related
  // when we're already in a wake flow or when probing /status.
  if (status === 500) {
    return sleeping || isStatusRequest;
  }
  return false;
}

@Injectable({
  providedIn: 'root'
})
export class HibernationFlowService {
  // HibernationService handles wake mechanics. This service handles app/request policy.
  private readonly hibernationService: HibernationService = inject(HibernationService);
  private readonly backendSelectorVisibleSignal = signal(false);

  readonly backendSelectorVisible = this.backendSelectorVisibleSignal.asReadonly();

  // Used by AppComponent and PrimusBackendInstanceService to gate wake/polling behavior
  // while the museum selector is visible.
  setBackendSelectorVisible(visible: boolean): void {
    this.backendSelectorVisibleSignal.set(visible);
  }

  // Used by JobStatusSubscriberService before each polling tick.
  // Pause polling while wake/selector/error states are active.
  shouldPauseBackgroundPolling(): boolean {
    const isSleeping = this.hibernationService.isSleeping();
    const selectorVisible = this.backendSelectorVisible();
    const wakeFlowActive = this.hibernationService.isWakeFlowActive();
    const wakeupError = this.hibernationService.hasRecentWakeupError();
    return isSleeping || selectorVisible || wakeFlowActive || !!wakeupError;
  }

  // Used by JobStatusSubscriberService to skip (re)starting timer loops.
  // Reuse the same pause policy to avoid races where polling starts while wake is active.
  shouldSkipBackgroundPollingStart(): boolean {
    return this.shouldPauseBackgroundPolling();
  }

  // Used by CmsApiService for diagnostics after ensureAwake().
  wakeupError(): string | null {
    return this.hibernationService.wakeupError();
  }

  // Used by CmsApiService.startHealthCheckTimeout.
  // Decides whether a slow-request wake probe should be skipped or throttled.
  shouldSkipSlowRequestWakeCheck(lastWakeCheckAt: number, throttleMs: number): 'skip-health-check' | 'wakeup-error' | 'throttled' | null {
    if (this.backendSelectorVisible()) {
      // User is in backend-selection flow.
      return 'skip-health-check';
    }
    if (this.hibernationService.skipHealthCheck) {
      // Health checks are disabled in current context.
      return 'skip-health-check';
    }
    if (this.hibernationService.hasRecentWakeupError()) {
      // A recent failed wake attempt just happened. Avoid immediately re-triggering wake checks.
      return 'wakeup-error';
    }
    if (Date.now() - lastWakeCheckAt < throttleMs) {
      // Wake check already ran recently.
      return 'throttled';
    }
    return null;
  }

  // Public wake entrypoint used by:
  // - CmsApiService slow-request checks
  // - JobStatusSubscriberService error recovery
  async ensureAwake(reason: string): Promise<boolean> {
    return this.hibernationService.ensureAwake(reason);
  }

  // Used by PrimusBackendInstanceService.apiAuthenticate when auth must fail fast
  // on wake failure.
  async ensureAwakeOrThrow(reason: string): Promise<void> {
    await this.ensureAwake(reason);
    const wakeupError = this.hibernationService.wakeupError();
    if (wakeupError) {
      throw new Error(wakeupError);
    }
  }

  // Run wake check only when app is already marked as sleeping.
  private async ensureAwakeIfSleeping(reason: string): Promise<void> {
    if (this.hibernationService.isSleeping()) {
      // Keep one cooldown window quiet after wake timeout/error to avoid repeated wake loops.
      if (this.hibernationService.hasRecentWakeupError()) {
        return;
      }
      await this.ensureAwake(reason);
    }
  }

  // Shared HTTP policy:
  // - pre-wake only when sleeping
  // - retry once after hibernation-like statuses
  // - retry once after network errors while waking
  // Used by CmsApiService for fetch and stream requests.
  async fetchWithWakeRecovery(
    params: HibernationRetryParams,
    callName: string,
    isStatusRequest: boolean,
    request: () => Promise<Response>,
    onStatusRetry: () => void = () => undefined,
    onNetworkRetry: () => void = () => undefined
  ): Promise<Response> {
    await this.ensureAwakeIfSleeping(`pre-request:${callName}`);
    try {
      let response = await request();
      if (!response.ok) {
        const wakeRetryResponse = await this.maybeWakeAndRetryOnce(
          params,
          response.status,
          isStatusRequest,
          `retry:${callName}:${response.status}`,
          request,
          onStatusRetry
        );
        if (wakeRetryResponse) {
          response = wakeRetryResponse;
        }
      }
      return response;
    } catch (error) {
      if (error instanceof FetchTimeoutError) {
        throw error;
      }
      const wakeRetryResponse = await this.retryAfterWakeFromNetworkError(
        params,
        `network-retry:${callName}`,
        request,
        onNetworkRetry
      );
      if (wakeRetryResponse) {
        return wakeRetryResponse;
      }
      throw error;
    }
  }

  // Used by JobStatusSubscriberService catch handler to trigger wake in background.
  // Returns true when error is handled as hibernation recovery.
  handleBackgroundPollingError(status: number | null | undefined, wakeReason: string): boolean {
    if (!this.shouldAttemptRecovery(status)) {
      return false;
    }
    this.ensureAwake(wakeReason).catch(() => undefined);
    return true;
  }

  // Internal wrapper around shared status policy with current sleeping state.
  private shouldAttemptRecovery(status: number | null | undefined, isStatusRequest: boolean = false): boolean {
    return shouldAttemptHibernationRecovery(status, this.hibernationService.isSleeping(), isStatusRequest);
  }

  // Internal: on hibernation-like HTTP status, wake once and retry request once.
  private async maybeWakeAndRetryOnce(
    params: HibernationRetryParams,
    status: number | null | undefined,
    isStatusRequest: boolean,
    wakeReason: string,
    retry: () => Promise<Response>,
    onRetry: () => void
  ): Promise<Response | null> {
    if (params._hibernationRetried) {
      return null;
    }
    params._hibernationRecoveryAttempted = true;
    if (!this.shouldAttemptRecovery(status, isStatusRequest)) {
      return null;
    }
    const didWakeFromHibernation = await this.ensureAwake(wakeReason);
    if (!didWakeFromHibernation) {
      return null;
    }
    params._hibernationRetried = true;
    onRetry();
    return retry();
  }

  // Internal: on network errors (for example connection reset), try wake once and retry once.
  private async retryAfterWakeFromNetworkError(
    params: HibernationRetryParams,
    wakeReason: string,
    retry: () => Promise<Response>,
    onRetry: () => void
  ): Promise<Response | null> {
    if (params._hibernationRetried) {
      return null;
    }
    params._hibernationRecoveryAttempted = true;

    try {
      const didWakeFromHibernation = await this.ensureAwake(wakeReason);
      if (!didWakeFromHibernation) {
        return null;
      }
      params._hibernationRetried = true;
      onRetry();
      return await retry();
    } catch {
      return null;
    }
  }

  // Used by CmsApiService error handlers to avoid duplicate error toasts while
  // hibernation recovery is already providing user feedback.
  shouldSuppressGlobalErrorToast(params: any, rejectedResponse: any): boolean {
    const status = rejectedResponse?.status;
    if (this.hibernationService.isSleeping() || this.hibernationService.isWakeFlowActive()) {
      return true;
    }
    if (this.hibernationService.hasRecentWakeupError()) {
      if (status == null) {
        return true;
      }
      return shouldAttemptHibernationRecovery(status, true);
    }
    if (params?._hibernationRecoveryAttempted) {
      if (status == null) {
        return true;
      }
      if (shouldAttemptHibernationRecovery(status, true)) {
        return true;
      }
    }
    if (!params?._hibernationRetried) {
      return false;
    }
    if (status == null) {
      return true;
    }
    return shouldAttemptHibernationRecovery(status, true);
  }
}
